import React from "react";
import styled from "styled-components";
import {
  changelogData,
  formatVersionType,
  getChangeTypeColor,
} from "../../data/changelog";

const ChangelogContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem;
  background: var(--background-color);
  min-height: 100vh;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 1rem;
  color: var(--text-color);
  font-weight: 800;
  background: linear-gradient(
    135deg,
    var(--primary-color),
    var(--success-color)
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.3rem;
  color: var(--text-secondary-color);
  margin-bottom: 3rem;
  font-weight: 500;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const VersionSection = styled.div`
  margin-bottom: 2.5rem;
  padding: 1.5rem;
  border: 1px solid var(--border-color);
  background: var(--surface-color);
  border-radius: 8px;
  box-shadow: 0 1px 3px var(--shadow-color);
`;

const VersionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-color);
  flex-wrap: wrap;
  gap: 1rem;
`;

const VersionTitle = styled.h2`
  font-size: 1.8rem;
  color: var(--text-color);
  margin: 0;
  font-weight: 700;
`;

const VersionDate = styled.span`
  color: var(--text-secondary-color);
  font-size: 0.95rem;
  font-weight: 500;
`;

const VersionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const VersionTag = styled.span<{ $type: "major" | "minor" | "patch" }>`
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: ${(props) => {
    switch (props.$type) {
      case "major":
        return "var(--error-color)";
      case "minor":
        return "var(--primary-color)";
      case "patch":
        return "var(--success-color)";
      default:
        return "var(--secondary-color)";
    }
  }};
  color: white;
`;

const ChangeItem = styled.div<{ $type: string }>`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  padding: 0.75rem;
  background: var(--surface-color);
  border-radius: 6px;
  border-left: 4px solid ${(props) => getChangeTypeColor(props.$type)};
  transition: all 0.2s ease;

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 2px 8px var(--shadow-color);
  }
`;

const ChangeType = styled.span<{ $type: string }>`
  font-weight: 600;
  color: ${(props) => getChangeTypeColor(props.$type)};
  min-width: fit-content;
  font-size: 0.9rem;
`;

const ChangeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Link = styled.a`
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s ease;

  &:hover {
    color: var(--primary-hover-color);
    text-decoration: underline;
  }
`;

const ChangelogPage: React.FC = () => {
  return (
    <ChangelogContainer>
      <PageTitle>Changelog</PageTitle>
      <PageSubtitle>
        Track the development progress and updates to NewME.social
        <br />
        <small style={{ fontSize: "0.9rem", opacity: 0.8 }}>
          Last updated: {changelogData.lastUpdated}
        </small>
      </PageSubtitle>

      {changelogData.versions.map((version) => (
        <VersionSection key={version.version}>
          <VersionHeader>
            <div>
              <VersionTitle>v{version.version}</VersionTitle>
              {version.description && (
                <p
                  style={{
                    color: "var(--text-secondary-color)",
                    margin: "0.5rem 0 0 0",
                  }}
                >
                  {version.description}
                  {version.preRelease && version.preReleaseTag && (
                    <span
                      style={{
                        marginLeft: "0.5rem",
                        padding: "0.2rem 0.4rem",
                        backgroundColor: "var(--warning-color)",
                        color: "white",
                        borderRadius: "4px",
                        fontSize: "0.8rem",
                        fontWeight: "600",
                      }}
                    >
                      {version.preReleaseTag}
                    </span>
                  )}
                </p>
              )}
            </div>
            <VersionMeta>
              <VersionTag $type={version.type}>{version.type}</VersionTag>
              <VersionDate>{version.date}</VersionDate>
            </VersionMeta>
          </VersionHeader>

          <ChangeList>
            {version.changes.map((change, index) => (
              <ChangeItem key={index} $type={change.type}>
                <ChangeType $type={change.type}>
                  {formatVersionType(change.type)}:
                </ChangeType>{" "}
                {change.description}
                {change.breaking && (
                  <span
                    style={{
                      marginLeft: "0.5rem",
                      padding: "0.2rem 0.4rem",
                      backgroundColor: "var(--error-color)",
                      color: "white",
                      borderRadius: "4px",
                      fontSize: "0.7rem",
                      fontWeight: "600",
                    }}
                  >
                    BREAKING
                  </span>
                )}
              </ChangeItem>
            ))}
          </ChangeList>
        </VersionSection>
      ))}

      <div
        style={{
          textAlign: "center",
          marginTop: "3rem",
          color: "var(--text-secondary-color)",
        }}
      >
        <p>
          Have a feature request or found a bug? Visit our{" "}
          <Link href="/contact">Contact page</Link> to let us know!
        </p>
      </div>
    </ChangelogContainer>
  );
};

export default ChangelogPage;
